import { VariantProps, CSS } from '@stitches/react';
import { FC, useEffect } from 'react';

import PopoverElement from '@/elements/Popover';
import useClickout from '@/hooks/useClickout';

export type PopoverVariants = Omit<
  VariantProps<typeof PopoverElement>,
  'status'
>;

export type Props = PopoverVariants & {
  as?: any;
  disableClickout?: boolean;
  id?: string;
  isOpen: boolean;
  onRequestClose: () => void;
  css?: CSS;
  // Inline style, forwarded to the underlying element (already passed through
  // via `...rest`). Used for measured/dynamic placement (e.g. TierPopover's
  // viewport-clamped fixed positioning); the type just makes that explicit.
  style?: CSSProperties;
  // This React's FC does not inject children, and the component renders them —
  // declare them so every `<Popover>…</Popover>` usage type-checks.
  children?: ReactNode;
};

const Popover: FC<Props> = ({
  align = 'top',
  as = 'div',
  children,
  disableClickout = false,
  isOpen,
  id,
  justify = 'left',
  margin = 'none',
  onRequestClose,
  ...rest
}) => {
  const clickoutRef = useClickout<HTMLDivElement>(onRequestClose, {
    debugName: `Popover(${id || '<no id>'})`,
    isActive: !disableClickout && isOpen,
  });

  // Escape dismissal (fix round 3): useClickout listens only for window
  // clicks, so keyboard users had no way to close an open popover. Lives here
  // (not in useClickout) so every Popover gets it in one place, and it is NOT
  // gated behind disableClickout — Escape should work even where clickout is
  // disabled.
  useEffect(() => {
    if (!isOpen) return undefined;

    const onKeyDown = (ev: KeyboardEvent) => {
      if (ev.key === 'Escape') onRequestClose();
    };
    window.addEventListener('keydown', onKeyDown);

    return () => window.removeEventListener('keydown', onKeyDown);
  }, [isOpen, onRequestClose]);

  return (
    <PopoverElement
      ref={clickoutRef}
      {...rest}
      align={align}
      aria-hidden={!isOpen}
      as={as}
      id={id}
      justify={justify}
      margin={margin}
      status={isOpen ? 'open' : 'closed'}
    >
      {children}
    </PopoverElement>
  );
};

export default Popover;
